import {
  AfterViewInit,
  Component,
  Inject,
  Injector,
  OnInit,
  Optional,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { forkJoin } from "rxjs";
import { User } from "src/app/models/user.model";
import { CommonService } from "src/app/services/common.service";
import { ToastService } from "src/app/services/toast.service";
import { UserService } from "src/app/services/user.service";
import nameOf from "src/app/shared/form/nameOf.helper";
import {
    IUserFormDialogData,
    UserDialogComponent,
} from "src/app/modals/user-dialog/user-dialog.component";
import { DeleteDialog } from "src/app/shared/delete-dialog/delete-dialog.component";
import { CoreGridComponent } from "src/app/shared/grid/core-grid/core-grid.component";
import { AuthService } from "src/app/services/auth.service";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { Observable, of } from "rxjs";
import { usersRouteConfig } from "src/app/shared/constants/usersRouteConfig.constants";
import { GridViewService } from "src/app/services/grid-view.service";
import { GridSettingsService } from "src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service";
import { ColumnConfig } from "src/app/shared/grid/core-grid/columnConfig.model";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { UserRoleService } from "src/app/services/userRole.service";
import { Lookup } from "src/app/models/lookup.model";
import { ISearchFieldData } from "src/app/shared/grid/baseGrid.component";
import { UserAccountSecurityPopupComponent } from "./user-account-security-popup/user-account-security-popup.component";
import { FlyoutContainerComponent } from "src/app/shared/flyout/flyout-container/flyout-container.component";

@Component({
  standalone: false,
    selector: "app-user-list",
    templateUrl: "./../../shared/grid/core-grid/core-grid.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./../../shared/grid/core-grid/core-grid.component.scss",
        "./user-list.component.scss",
    ],
})
export class UserListComponent
    extends CoreGridComponent<User>
    implements OnInit, AfterViewInit
{
    @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

    organizationLevels: Lookup[] = [];
    userRoles: Lookup[] = [];

    constructor(
        protected userService: UserService,
        toastService: ToastService,
        commonService: CommonService,
        dialog: MatDialog,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        private organizationLevelService: OrganizationLevelService,
        private userRoleService: UserRoleService,
        protected injector: Injector,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public searchFieldData: ISearchFieldData,
    ) {
        super(
            toastService,
            dialog,
            commonService,
            authService,
            gridViewService,
            gridSettingsService,
        );
        this.gridService = userService;
    }

    protected get objectId(): string {
        return RolePermissionObjects.User;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    override initializeProperties(): void {
        this.addEntityTitle = "User";
        this.sortActive = nameOf<User>((x) => x.userName);
        this.defaultOrderBy = "asc";
        this.searchPlaceholder = "Search Users";
    }

    override initializeColumns(): void {
        this.additionalActions = [];
        this.columnConfigs = [
            new ColumnConfig<User>({
                name: nameOf<User>((x) => x.userName),
                displayName: "User Name",
                cssClass: () => "left-aligned cursor-pointer user-name-security-link",
                cellTemplate: (user: User) => user.userName || "-",
                onClick: (user: User) => {
                    if (user?.id) {
                        this.openFlyoutInternal(
                            UserAccountSecurityPopupComponent,
                            user.id,
                            "480px",
                            true,
                        );
                    }
                },
            }),
            new ColumnConfig<User>({
                name: nameOf<User>((x) => x.fullName),
                displayName: "Full Name",
                cellTemplate: (user: User) => user.fullName || "N/A",
            }),
            new ColumnConfig<User>({
                name: nameOf<User>((x) => x.phoneNumber),
                displayName: "Phone Number",
                cellTemplate: (user: User) => user.phoneNumber || "N/A",
            }),
            new ColumnConfig<User>({
                name: nameOf<User>((x) => x.jobTitle),
                displayName: "Job Title",
                cellTemplate: (user: User) => user.jobTitle || "N/A",
            }),
            new ColumnConfig<User>({
                name: nameOf<User>((x) => x.organizationLevelIds),
                displayName: "Visibility",
                cssClass: (user: User) =>
                    user.organizationLevelIds?.length > 1
                        ? "multiple-data-border"
                        : "",
                cellTemplate: (user: User) => {
                    if (!user.organizationLevelIds?.length) return "N/A";
                    const organizationNames = user.organizationLevelIds.map(
                        (id) =>
                            this.organizationLevels.find((o) => o.id === id)
                                ?.name,
                    );
                    return organizationNames
                        .map((org) => `<div>${org ?? "-"}</div>`)
                        .join("");
                },
            }),
            new ColumnConfig<User>({
                name: nameOf<User>((x) => x.roleId),
                displayName: "Access Role",
                cellTemplate: (user: User) => {
                    const role = this.userRoles.find(
                        (r) => r.id === user.roleId,
                    );
                    return role ? role.name : "N/A";
                },
            }),
            { name: "action", displayName: "Action" },
        ];
    }

    get hasOrgFilter() {
        return true;
    }

    override objectFilterMap(propertyName: string): any | undefined {
        switch (propertyName) {
            case nameOf<User>((x) => x.organizationLevelIds):
                return this.organizationLevelService;
            case nameOf<User>((x) => x.roleId):
                return this.userRoleService;
            default:
                return undefined;
        }
    }

    ngOnInit(): void {
        try {
            this.searchDialogRef = this.injector.get(
                MatDialogRef<UserListComponent>,
            );
        } catch {
            // This component can be used without a dialog host.
        }
        super.ngOnInit();

        forkJoin({
            organizationLevels: this.organizationLevelService.getAll(),
            userRoles: this.userRoleService.getAll(),
        }).subscribe((results) => {
            this.organizationLevels = results.organizationLevels;
            this.userRoles = results.userRoles;
        });
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.loadHeaderData();
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override initializeTabs() {
        return usersRouteConfig;
    }

    override getOverviewData(): Observable<void> {
        return of(undefined);
    }

    override openEntityDialog(entityId?: string): void {
        this.openUserDialog(entityId);
    }

    override openEntityDeleteDialog(entity: User): void {
        this.openUserDeleteDialog(entity);
    }

    openUserDeleteDialog(user: User) {
        this.openDeleteDialog(DeleteDialog, user, this.userService, "User");
    }

    openUserDialog(userId?: string) {
        const userModel: IUserFormDialogData = {
            id: userId ?? '',
            organizationLevelIds: this.selectedOrg?.id
                ? [this.selectedOrg?.id]
                : [],
        };

        this.openDialog(UserDialogComponent, userModel);
    }
}
